import React, { useState } from "react";
import { C, ROLE_PERMISSIONS, TRANSACTION_TYPES, TRANSACTION_STATUSES, PAYMENT_METHODS } from "./constants";
import {
  Card, Btn, Field, Modal, ModuleHeader, KPICard, TabBar,
  StatusBadge, DataTable, MeetingsTab, ReportsTab, useToast
} from "./erp-shared";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, AreaChart, Area, CartesianGrid
} from "recharts";
import {
  Wallet, DollarSign, TrendingUp, TrendingDown, Plus, Search,
  CheckCircle, Clock, AlertCircle, FileText, Calendar, Target,
  CreditCard, Receipt, Building, Users, ArrowUpRight, ArrowDownRight,
  Download, Printer, Filter, BarChart3, PieChart as PieIcon,
  Landmark, CircleDot, Hash, Layers, Eye, Star
} from "lucide-react";

/* ═════════════════════════════════════════════
   ✦ Finance Page — المالية
   ═════════════════════════════════════════════ */
export default function FinancePage({ user }) {
  const [tab, setTab] = useState("overview");
  const perms = ROLE_PERMISSIONS[user.role] || {};

  const tabs = [
    { key: "overview", label: "نظرة عامة", icon: Wallet },
    { key: "income", label: "الإيرادات", icon: TrendingUp },
    { key: "expenses", label: "المصروفات", icon: TrendingDown },
    { key: "budgets", label: "الميزانيات", icon: Target },
    { key: "accounts", label: "الحسابات", icon: Building },
    { key: "meetings", label: "الاجتماعات", icon: Calendar },
    { key: "reports", label: "التقارير", icon: FileText },
  ];

  return (
    <div>
      <ModuleHeader icon={Wallet} title="المالية" subtitle="إدارة الإيرادات والمصروفات والميزانيات والحسابات" />
      <TabBar tabs={tabs} active={tab} onChange={setTab} />

      {tab === "overview" && <OverviewTab />}
      {tab === "income" && <IncomeTab canEdit={perms.canEdit} />}
      {tab === "expenses" && <ExpensesTab canEdit={perms.canEdit} />}
      {tab === "budgets" && <BudgetsTab canEdit={perms.canEdit} />}
      {tab === "accounts" && <AccountsTab />}
      {tab === "meetings" && <MeetingsTab departmentName="المالية" />}
      {tab === "reports" && <ReportsTab departmentName="المالية" />}
    </div>
  );
}

/* ─────────────────────────────────────────────
   ✦ Overview Tab — نظرة عامة مالية
   ───────────────────────────────────────────── */
function OverviewTab() {
  const totalIncome = MONTHLY_DATA.reduce((s, m) => s + m.income, 0);
  const totalExpense = MONTHLY_DATA.reduce((s, m) => s + m.expense, 0);
  const balance = totalIncome - totalExpense;

  return (
    <div style={{ marginTop: 16 }}>
      <div style={styles.kpiRow}>
        <KPICard title="إجمالي الإيرادات" value={`${(totalIncome / 1000000).toFixed(2)}M`} icon={TrendingUp} color={C.success} trend="+12%" />
        <KPICard title="إجمالي المصروفات" value={`${(totalExpense / 1000000).toFixed(2)}M`} icon={TrendingDown} color={C.danger} trend="-5%" />
        <KPICard title="صافي الرصيد" value={`${(balance / 1000).toFixed(0)}K`} icon={DollarSign} color={C.teal} />
        <KPICard title="التبرعات" value="1.2M" icon={CreditCard} color={C.accent} trend="+18%" />
        <KPICard title="المعاملات" value="248" icon={Receipt} color="#7c3aed" />
        <KPICard title="الحسابات البنكية" value="4" icon={Building} color={C.primary} />
      </div>

      {/* Charts */}
      <div style={styles.chartsRow}>
        <Card style={{ padding: 20, flex: 1 }}>
          <h4 style={styles.chartTitle}><BarChart3 size={18} color={C.teal} /> الإيرادات والمصروفات الشهرية</h4>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={MONTHLY_DATA} barGap={4}>
              <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
              <XAxis dataKey="month" fontSize={11} tick={{ fill: C.textLight }} />
              <YAxis fontSize={11} tick={{ fill: C.textLight }} tickFormatter={v => `${(v / 1000).toFixed(0)}k`} />
              <Tooltip contentStyle={{ borderRadius: 8, border: `1px solid ${C.border}`, fontFamily: "'Readex Pro'" }} formatter={v => [`${v.toLocaleString()} ريال`]} />
              <Bar dataKey="income" name="الإيرادات" fill={C.success} radius={[4, 4, 0, 0]} />
              <Bar dataKey="expense" name="المصروفات" fill={C.danger} radius={[4, 4, 0, 0]} opacity={0.7} />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card style={{ padding: 20, flex: 1 }}>
          <h4 style={styles.chartTitle}><PieIcon size={18} color="#7c3aed" /> توزيع المصروفات</h4>
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie data={EXPENSE_DIST} cx="50%" cy="50%" innerRadius={50} outerRadius={90} dataKey="value" paddingAngle={3}>
                {EXPENSE_DIST.map((e, i) => <Cell key={i} fill={EXPENSE_COLORS[i]} />)}
              </Pie>
              <Tooltip contentStyle={{ borderRadius: 8, border: `1px solid ${C.border}`, fontFamily: "'Readex Pro'" }} formatter={v => [`${v.toLocaleString()} ريال`]} />
            </PieChart>
          </ResponsiveContainer>
          <div style={styles.legendGrid}>
            {EXPENSE_DIST.map((d, i) => (
              <div key={i} style={styles.legendItem}>
                <div style={{ width: 10, height: 10, borderRadius: 3, background: EXPENSE_COLORS[i] }} />
                <span style={{ fontSize: 11, color: C.textLight }}>{d.name}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Income trend */}
      <Card style={{ padding: 20, marginTop: 16 }}>
        <h4 style={styles.chartTitle}><TrendingUp size={18} color={C.success} /> تطور الإيرادات الشهرية</h4>
        <ResponsiveContainer width="100%" height={200}>
          <AreaChart data={MONTHLY_DATA}>
            <defs>
              <linearGradient id="incGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={C.success} stopOpacity={0.2} />
                <stop offset="95%" stopColor={C.success} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
            <XAxis dataKey="month" fontSize={11} tick={{ fill: C.textLight }} />
            <YAxis fontSize={11} tick={{ fill: C.textLight }} tickFormatter={v => `${(v / 1000).toFixed(0)}k`} />
            <Tooltip contentStyle={{ borderRadius: 8, fontFamily: "'Readex Pro'" }} formatter={v => [`${v.toLocaleString()} ريال`]} />
            <Area type="monotone" dataKey="income" stroke={C.success} fill="url(#incGrad)" strokeWidth={2.5} name="الإيرادات" />
          </AreaChart>
        </ResponsiveContainer>
      </Card>
    </div>
  );
}

/* ─────────────────────────────────────────────
   ✦ Income Tab — الإيرادات
   ───────────────────────────────────────────── */
const INITIAL_INCOME = { type: "", date: "", donor: "", amount: "", method: "", description: "" };

function IncomeTab({ canEdit }) {
  const [data, setData] = useState(DEMO_INCOME);
  const [search, setSearch] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [showAdd, setShowAdd] = useState(false);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(INITIAL_INCOME);
  const [showToast, ToastView] = useToast();

  const INCOME_TYPES = ["تبرع نقدي","تبرع عيني","وقف","زكاة","صدقة","كفالة","منحة حكومية","إيراد استثماري"];

  function handleSave() {
    if (!form.type || !form.date || !form.donor || !form.amount || !form.method) {
      showToast("يرجى تعبئة الحقول المطلوبة", "error");
      return;
    }
    const num = String(data.length + 1).padStart(3, "0");
    const newI = {
      id: `i${Date.now()}`,
      number: `إ-${num}`,
      date: form.date,
      type: form.type,
      donor: form.donor,
      amount: Number(form.amount) || 0,
      method: form.method,
      status: "معتمد",
      description: form.description,
    };
    setData(prev => [newI, ...prev]);
    setForm(INITIAL_INCOME);
    setShowAdd(false);
    showToast("تم تسجيل الإيراد");
  }
  const filtered = data.filter(d => {
    const matchSearch = d.description.includes(search) || d.donor.includes(search);
    const matchType = filterType === "all" || d.type === filterType;
    return matchSearch && matchType;
  });

  const total = filtered.reduce((s, d) => s + d.amount, 0);

  return (
    <div style={{ marginTop: 16 }}>
      {ToastView}
      <div style={styles.kpiRow}>
        <KPICard title="إجمالي الإيرادات" value={`${(total / 1000).toFixed(0)}K`} icon={TrendingUp} color={C.success} />
        <KPICard title="عدد المعاملات" value={filtered.length} icon={Receipt} color="#7c3aed" />
        <KPICard title="أكبر تبرع" value={`${(Math.max(...data.map(d => d.amount)) / 1000).toFixed(0)}K`} icon={Star} color={C.accent} />
        <KPICard title="متوسط التبرع" value={`${(total / data.length / 1000).toFixed(1)}K`} icon={BarChart3} color={C.teal} />
      </div>

      <Card style={{ padding: 16, marginBottom: 16 }}>
        <div style={styles.toolbar}>
          <div style={styles.searchBox}>
            <Search size={16} color={C.textLight} />
            <input type="text" placeholder="بحث بالوصف أو المتبرع..." value={search} onChange={e => setSearch(e.target.value)} style={styles.searchInput} />
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <select value={filterType} onChange={e => setFilterType(e.target.value)} style={styles.filterSelect}>
              <option value="all">جميع الأنواع</option>
              {INCOME_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            {canEdit && <Btn icon={Plus} onClick={() => setShowAdd(true)} size="sm">تسجيل إيراد</Btn>}
          </div>
        </div>
      </Card>

      <Card style={{ padding: 0, overflow: "hidden" }}>
        <DataTable
          columns={[
            { key: "number", label: "الرقم", width: "90px", render: v => <span style={{ fontWeight: 600, color: C.primary }}>{v}</span> },
            { key: "date", label: "التاريخ", width: "100px" },
            { key: "type", label: "النوع", width: "110px" },
            { key: "donor", label: "المتبرع / المصدر" },
            { key: "amount", label: "المبلغ (ريال)", width: "120px", render: v => <span style={{ fontWeight: 700, color: C.success }}>{v.toLocaleString()}</span> },
            { key: "method", label: "طريقة الدفع", width: "110px" },
            { key: "status", label: "الحالة", width: "100px", render: v => <StatusBadge status={v} /> },
          ]}
          data={filtered}
          onRowClick={row => setSelected(row)}
        />
      </Card>

      {/* Total bar */}
      <div style={styles.totalBar}>
        <span>الإجمالي المعروض:</span>
        <span style={{ fontWeight: 700, color: C.success, fontSize: 18 }}>{total.toLocaleString()} ريال</span>
      </div>

      {selected && (
        <Modal title={`إيراد — ${selected.number}`} onClose={() => setSelected(null)} width={500}>
          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            <div style={styles.detailRow}><strong>الرقم:</strong> {selected.number}</div>
            <div style={styles.detailRow}><strong>التاريخ:</strong> {selected.date}</div>
            <div style={styles.detailRow}><strong>النوع:</strong> {selected.type}</div>
            <div style={styles.detailRow}><strong>المتبرع:</strong> {selected.donor}</div>
            <div style={styles.detailRow}><strong>المبلغ:</strong> <span style={{ color: C.success, fontWeight: 700 }}>{selected.amount.toLocaleString()} ريال</span></div>
            <div style={styles.detailRow}><strong>طريقة الدفع:</strong> {selected.method}</div>
            <div style={styles.detailRow}><strong>الوصف:</strong> {selected.description}</div>
          </div>
        </Modal>
      )}

      {showAdd && (
        <Modal title="تسجيل إيراد جديد" onClose={() => { setShowAdd(false); setForm(INITIAL_INCOME); }} width={600}>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Field label="النوع" type="select" options={INCOME_TYPES} required value={form.type} onChange={v => setForm({ ...form, type: v })} />
              <Field label="التاريخ" type="date" required value={form.date} onChange={v => setForm({ ...form, date: v })} />
            </div>
            <Field label="المتبرع / المصدر" required placeholder="اسم المتبرع أو الجهة" value={form.donor} onChange={v => setForm({ ...form, donor: v })} />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Field label="المبلغ (ريال)" type="number" required placeholder="0" value={form.amount} onChange={v => setForm({ ...form, amount: v })} />
              <Field label="طريقة الدفع" type="select" options={PAYMENT_METHODS} required value={form.method} onChange={v => setForm({ ...form, method: v })} />
            </div>
            <Field label="الوصف" type="textarea" placeholder="تفاصيل الإيراد..." value={form.description} onChange={v => setForm({ ...form, description: v })} />
            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <Btn icon={TrendingUp} variant="success" onClick={handleSave}>تسجيل</Btn>
              <Btn variant="ghost" onClick={() => { setShowAdd(false); setForm(INITIAL_INCOME); }}>إلغاء</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   ✦ Expenses Tab — المصروفات
   ───────────────────────────────────────────── */
const INITIAL_EXPENSE = { department: "", date: "", description: "", amount: "", method: "", vendor: "", notes: "" };

function ExpensesTab({ canEdit }) {
  const [data, setData] = useState(DEMO_EXPENSES);
  const [search, setSearch] = useState("");
  const [filterDept, setFilterDept] = useState("all");
  const [showAdd, setShowAdd] = useState(false);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(INITIAL_EXPENSE);
  const [showToast, ToastView] = useToast();

  function handleSave() {
    if (!form.department || !form.date || !form.description || !form.amount || !form.method) {
      showToast("يرجى تعبئة الحقول المطلوبة", "error");
      return;
    }
    const num = String(data.length + 1).padStart(3, "0");
    const newE = {
      id: `e${Date.now()}`,
      number: `م-${num}`,
      date: form.date,
      department: form.department,
      description: form.description,
      amount: Number(form.amount) || 0,
      vendor: form.vendor || "—",
      method: form.method,
      status: "pending",
    };
    setData(prev => [newE, ...prev]);
    setForm(INITIAL_EXPENSE);
    setShowAdd(false);
    showToast("تم تسجيل المصروف");
  }

  const depts = [...new Set(data.map(d => d.department))];
  const filtered = data.filter(d => {
    const matchSearch = d.description.includes(search) || d.vendor.includes(search);
    const matchDept = filterDept === "all" || d.department === filterDept;
    return matchSearch && matchDept;
  });

  const total = filtered.reduce((s, d) => s + d.amount, 0);

  const EXP_STATUSES = [
    { key: "approved", label: "معتمد", color: C.success },
    { key: "pending", label: "بانتظار الاعتماد", color: C.warning },
    { key: "paid", label: "تم الصرف", color: C.teal },
    { key: "rejected", label: "مرفوض", color: C.danger },
  ];

  return (
    <div style={{ marginTop: 16 }}>
      {ToastView}
      <div style={styles.kpiRow}>
        <KPICard title="إجمالي المصروفات" value={`${(total / 1000).toFixed(0)}K`} icon={TrendingDown} color={C.danger} />
        <KPICard title="عدد المعاملات" value={filtered.length} icon={Receipt} color="#7c3aed" />
        <KPICard title="بانتظار الاعتماد" value={data.filter(d => d.status === "pending").length} icon={Clock} color={C.warning} />
        <KPICard title="تم الصرف" value={data.filter(d => d.status === "paid").length} icon={CheckCircle} color={C.teal} />
      </div>

      <Card style={{ padding: 16, marginBottom: 16 }}>
        <div style={styles.toolbar}>
          <div style={styles.searchBox}>
            <Search size={16} color={C.textLight} />
            <input type="text" placeholder="بحث..." value={search} onChange={e => setSearch(e.target.value)} style={styles.searchInput} />
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <select value={filterDept} onChange={e => setFilterDept(e.target.value)} style={styles.filterSelect}>
              <option value="all">جميع الأقسام</option>
              {depts.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
            {canEdit && <Btn icon={Plus} onClick={() => setShowAdd(true)} size="sm">تسجيل مصروف</Btn>}
          </div>
        </div>
      </Card>

      <Card style={{ padding: 0, overflow: "hidden" }}>
        <DataTable
          columns={[
            { key: "number", label: "الرقم", width: "90px", render: v => <span style={{ fontWeight: 600, color: C.primary }}>{v}</span> },
            { key: "date", label: "التاريخ", width: "100px" },
            { key: "department", label: "القسم", width: "110px" },
            { key: "description", label: "الوصف" },
            { key: "amount", label: "المبلغ (ريال)", width: "120px", render: v => <span style={{ fontWeight: 700, color: C.danger }}>{v.toLocaleString()}</span> },
            { key: "vendor", label: "المورد", width: "130px" },
            { key: "status", label: "الحالة", width: "120px", render: v => <StatusBadge status={v} statuses={EXP_STATUSES} /> },
          ]}
          data={filtered}
          onRowClick={row => setSelected(row)}
        />
      </Card>

      <div style={styles.totalBar}>
        <span>الإجمالي المعروض:</span>
        <span style={{ fontWeight: 700, color: C.danger, fontSize: 18 }}>{total.toLocaleString()} ريال</span>
      </div>

      {selected && (
        <Modal title={`مصروف — ${selected.number}`} onClose={() => setSelected(null)} width={500}>
          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            <div style={styles.detailRow}><strong>الرقم:</strong> {selected.number}</div>
            <div style={styles.detailRow}><strong>التاريخ:</strong> {selected.date}</div>
            <div style={styles.detailRow}><strong>القسم:</strong> {selected.department}</div>
            <div style={styles.detailRow}><strong>الوصف:</strong> {selected.description}</div>
            <div style={styles.detailRow}><strong>المبلغ:</strong> <span style={{ color: C.danger, fontWeight: 700 }}>{selected.amount.toLocaleString()} ريال</span></div>
            <div style={styles.detailRow}><strong>المورد:</strong> {selected.vendor}</div>
            <div style={styles.detailRow}><strong>طريقة الدفع:</strong> {selected.method}</div>
          </div>
        </Modal>
      )}

      {showAdd && (
        <Modal title="تسجيل مصروف جديد" onClose={() => { setShowAdd(false); setForm(INITIAL_EXPENSE); }} width={600}>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Field label="القسم" type="select" options={["الجاليات","المساجد","النسائي","الإدارة","تنمية الموارد","الاتصال","التطوع","الموارد البشرية"]} required value={form.department} onChange={v => setForm({ ...form, department: v })} />
              <Field label="التاريخ" type="date" required value={form.date} onChange={v => setForm({ ...form, date: v })} />
            </div>
            <Field label="الوصف" required placeholder="وصف المصروف" value={form.description} onChange={v => setForm({ ...form, description: v })} />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Field label="المبلغ (ريال)" type="number" required placeholder="0" value={form.amount} onChange={v => setForm({ ...form, amount: v })} />
              <Field label="طريقة الدفع" type="select" options={PAYMENT_METHODS} required value={form.method} onChange={v => setForm({ ...form, method: v })} />
            </div>
            <Field label="المورد" placeholder="اسم المورد أو الجهة" value={form.vendor} onChange={v => setForm({ ...form, vendor: v })} />
            <Field label="ملاحظات" type="textarea" placeholder="ملاحظات إضافية..." value={form.notes} onChange={v => setForm({ ...form, notes: v })} />
            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <Btn icon={TrendingDown} variant="danger" onClick={handleSave}>تسجيل</Btn>
              <Btn variant="ghost" onClick={() => { setShowAdd(false); setForm(INITIAL_EXPENSE); }}>إلغاء</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   ✦ Budgets Tab — الميزانيات
   ───────────────────────────────────────────── */
const BUDGET_DEPTS = ["الجاليات", "المساجد", "النسائي", "تنمية الموارد", "الموارد البشرية", "الاتصال", "التطوع", "الإدارة", "الحوكمة"];
const INITIAL_BUDGET = { department: "", item: "", approvedAmount: "", spentAmount: "", period: "1447", notes: "" };

function BudgetsTab({ canEdit }) {
  const [budgets, setBudgets] = useState(DEPT_BUDGETS);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(INITIAL_BUDGET);
  const [showToast, ToastView] = useToast();

  function handleSave() {
    if (!form.department || !form.item || !form.approvedAmount) {
      showToast("القسم والبند والمبلغ المعتمد مطلوبة", "error");
      return;
    }
    setBudgets(prev => [...prev, {
      name: `${form.department} — ${form.item}`,
      budget: Number(form.approvedAmount) || 0,
      spent: Number(form.spentAmount) || 0,
      color: "#7c3aed",
      department: form.department,
      item: form.item,
      period: form.period,
      notes: form.notes,
    }]);
    setForm(INITIAL_BUDGET);
    setShowAdd(false);
    showToast("تمت إضافة بند الميزانية");
  }

  return (
    <div style={{ marginTop: 16 }}>
      {ToastView}
      <div style={styles.kpiRow}>
        <KPICard title="الميزانية الإجمالية" value="4.2M" icon={Target} color={C.primary} />
        <KPICard title="المنصرف" value="1.87M" icon={TrendingDown} color={C.danger} />
        <KPICard title="المتبقي" value="2.33M" icon={DollarSign} color={C.success} />
        <KPICard title="نسبة الإنفاق" value="44.5%" icon={BarChart3} color={C.warning} />
      </div>

      {canEdit && (
        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 16 }}>
          <Btn icon={Plus} onClick={() => setShowAdd(true)} size="sm">إضافة بند ميزانية</Btn>
        </div>
      )}

      <Card style={{ padding: 20 }}>
        <h4 style={styles.chartTitle}><Target size={18} color={C.primary} /> ميزانية الأقسام — 1446هـ</h4>
        <div style={{ display: "flex", flexDirection: "column", gap: 20, marginTop: 16 }}>
          {budgets.map((d, i) => {
            const pct = Math.round((d.spent / d.budget) * 100);
            return (
              <div key={i} style={styles.budgetRow}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, width: 180 }}>
                  <div style={{ ...styles.budgetDot, background: d.color }} />
                  <span style={{ fontSize: 14, fontWeight: 600, color: C.primary }}>{d.name}</span>
                </div>
                <div style={{ flex: 1 }}>
                  <div style={styles.budgetBarBg}>
                    <div style={{
                      ...styles.budgetBarFill,
                      width: `${pct}%`,
                      background: pct > 80 ? C.danger : pct > 60 ? C.warning : d.color,
                    }} />
                  </div>
                </div>
                <div style={{ display: "flex", gap: 16, fontSize: 13, minWidth: 280 }}>
                  <span style={{ color: C.textLight }}>الميزانية: <strong style={{ color: C.primary }}>{d.budget.toLocaleString()}</strong></span>
                  <span style={{ color: C.textLight }}>المنصرف: <strong style={{ color: C.danger }}>{d.spent.toLocaleString()}</strong></span>
                  <span style={{
                    fontWeight: 700,
                    color: pct > 80 ? C.danger : pct > 60 ? C.warning : C.success,
                  }}>{pct}%</span>
                </div>
              </div>
            );
          })}
        </div>
      </Card>

      {showAdd && (
        <Modal title="إضافة بند ميزانية" onClose={() => { setShowAdd(false); setForm(INITIAL_BUDGET); }} width={580}>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <Field label="القسم" type="select" options={BUDGET_DEPTS} required value={form.department} onChange={v => setForm({ ...form, department: v })} />
            <Field label="البند" required placeholder="مثال: برامج تعليمية" value={form.item} onChange={v => setForm({ ...form, item: v })} />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
              <Field label="المبلغ المعتمد (ر.س)" type="number" required placeholder="0" value={form.approvedAmount} onChange={v => setForm({ ...form, approvedAmount: v })} />
              <Field label="المنصرف حتى الآن" type="number" placeholder="0" value={form.spentAmount} onChange={v => setForm({ ...form, spentAmount: v })} />
              <Field label="السنة المالية" placeholder="1447" value={form.period} onChange={v => setForm({ ...form, period: v })} />
            </div>
            <Field label="ملاحظات" type="textarea" rows={2} placeholder="ملاحظات إضافية..." value={form.notes} onChange={v => setForm({ ...form, notes: v })} />
            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <Btn icon={Target} onClick={handleSave}>حفظ</Btn>
              <Btn variant="ghost" onClick={() => { setShowAdd(false); setForm(INITIAL_BUDGET); }}>إلغاء</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   ✦ Accounts Tab — الحسابات البنكية
   ───────────────────────────────────────────── */
function AccountsTab() {
  return (
    <div style={{ marginTop: 16 }}>
      <div style={styles.accountsGrid}>
        {BANK_ACCOUNTS.map((a, i) => (
          <Card key={i} style={styles.accountCard}>
            <div style={styles.accountHeader}>
              <div style={{ ...styles.accountIcon, background: `${a.color}15` }}>
                <Building size={24} color={a.color} />
              </div>
              <div>
                <h4 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: C.primary }}>{a.bank}</h4>
                <p style={{ margin: "2px 0 0", fontSize: 12, color: C.textLight }}>{a.type} — {a.accountNumber}</p>
              </div>
            </div>
            <div style={styles.accountBalance}>
              <span style={{ fontSize: 12, color: C.textLight }}>الرصيد الحالي</span>
              <span style={{ fontSize: 28, fontWeight: 700, color: a.color }}>{a.balance.toLocaleString()}</span>
              <span style={{ fontSize: 12, color: C.textLight }}>ريال سعودي</span>
            </div>
            <div style={styles.accountMeta}>
              <div style={styles.accountMetaItem}>
                <span style={{ fontSize: 11, color: C.textLight }}>آخر حركة</span>
                <span style={{ fontSize: 13, fontWeight: 600, color: C.primary }}>{a.lastTransaction}</span>
              </div>
              <div style={styles.accountMetaItem}>
                <span style={{ fontSize: 11, color: C.textLight }}>الحالة</span>
                <span style={{ fontSize: 13, fontWeight: 600, color: C.success }}>نشط</span>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

/* ═════════════════════════════════════════════
   ✦ Demo Data
   ═════════════════════════════════════════════ */
const MONTHLY_DATA = [
  { month: "محرم", income: 280000, expense: 210000 },
  { month: "صفر", income: 310000, expense: 240000 },
  { month: "ربيع1", income: 350000, expense: 260000 },
  { month: "ربيع2", income: 290000, expense: 230000 },
  { month: "جمادى1", income: 380000, expense: 280000 },
  { month: "جمادى2", income: 420000, expense: 310000 },
  { month: "رجب", income: 360000, expense: 290000 },
  { month: "شعبان", income: 450000, expense: 320000 },
  { month: "رمضان", income: 680000, expense: 380000 },
  { month: "شوال", income: 320000, expense: 270000 },
  { month: "ذوالقعدة", income: 340000, expense: 250000 },
  { month: "ذوالحجة", income: 410000, expense: 300000 },
];

const EXPENSE_DIST = [
  { name: "رواتب", value: 720000 },
  { name: "صيانة مساجد", value: 350000 },
  { name: "برامج جاليات", value: 280000 },
  { name: "فعاليات", value: 220000 },
  { name: "إدارية", value: 180000 },
  { name: "أخرى", value: 120000 },
];
const EXPENSE_COLORS = [C.primary, "#0891b2", "#2563eb", "#ea580c", "#7c3aed", C.textLight];

const DEMO_INCOME = [
  { id: "i1", number: "إ-001", date: "1446/09/01", type: "تبرع نقدي", donor: "فهد بن سعود المطيري", amount: 100000, method: "تحويل بنكي", status: "معتمد", description: "تبرع لدعم برامج رمضان" },
  { id: "i2", number: "إ-002", date: "1446/09/03", type: "تبرع نقدي", donor: "شركة النور التجارية", amount: 250000, method: "تحويل بنكي", status: "معتمد", description: "تبرع مؤسسي لإفطار الصائمين" },
  { id: "i3", number: "إ-003", date: "1446/09/05", type: "زكاة", donor: "متبرع مجهول", amount: 50000, method: "نقدي", status: "معتمد", description: "زكاة مال" },
  { id: "i4", number: "إ-004", date: "1446/08/20", type: "كفالة", donor: "عبدالله الصالح", amount: 36000, method: "تحويل بنكي", status: "معتمد", description: "كفالة داعية لمدة سنة" },
  { id: "i5", number: "إ-005", date: "1446/08/15", type: "منحة حكومية", donor: "وزارة الموارد البشرية", amount: 180000, method: "تحويل بنكي", status: "معتمد", description: "دعم سنوي للجمعية" },
  { id: "i6", number: "إ-006", date: "1446/07/10", type: "تبرع نقدي", donor: "محمد الحربي", amount: 25000, method: "بطاقة ائتمانية", status: "معتمد", description: "تبرع عام" },
  { id: "i7", number: "إ-007", date: "1446/09/10", type: "صدقة", donor: "متعددون", amount: 42000, method: "نقدي", status: "بانتظار المراجعة", description: "صدقات صناديق المساجد — رمضان" },
  { id: "i8", number: "إ-008", date: "1446/09/12", type: "تبرع عيني", donor: "مصنع الأغذية الوطني", amount: 35000, method: "عيني", status: "معتمد", description: "توفير 2000 وجبة إفطار" },
];

const DEMO_EXPENSES = [
  { id: "e1", number: "م-001", date: "1446/09/01", department: "الجاليات", description: "رواتب الدعاة — شهر رمضان", amount: 95000, vendor: "الموارد البشرية", method: "تحويل بنكي", status: "paid" },
  { id: "e2", number: "م-002", date: "1446/08/25", department: "المساجد", description: "صيانة تكييف مسجد الفرقان", amount: 45000, vendor: "مؤسسة التبريد الحديثة", method: "تحويل بنكي", status: "paid" },
  { id: "e3", number: "م-003", date: "1446/09/01", department: "الإدارة", description: "إيجار مقر الجمعية — رمضان", amount: 15000, vendor: "شركة العقارات المتحدة", method: "تحويل بنكي", status: "paid" },
  { id: "e4", number: "م-004", date: "1446/09/05", department: "تنمية الموارد", description: "مواد حملة إفطار الصائمين", amount: 85000, vendor: "مصنع الأغذية المحلي", method: "شيك", status: "approved" },
  { id: "e5", number: "م-005", date: "1446/09/08", department: "الاتصال", description: "طباعة بروشورات ولوحات", amount: 12000, vendor: "مطابع النور", method: "نقدي", status: "paid" },
  { id: "e6", number: "م-006", date: "1446/09/10", department: "المساجد", description: "تجديد سجاد مسجد البر", amount: 35000, vendor: "مؤسسة الفرش", method: "تحويل بنكي", status: "pending" },
  { id: "e7", number: "م-007", date: "1446/08/15", department: "النسائي", description: "مستلزمات حلقات التحفيظ النسائية", amount: 8000, vendor: "مكتبة التقوى", method: "نقدي", status: "paid" },
  { id: "e8", number: "م-008", date: "1446/09/12", department: "الجاليات", description: "مواد تعليمية متعددة اللغات", amount: 18000, vendor: "دار المعرفة", method: "تحويل بنكي", status: "pending" },
];

const DEPT_BUDGETS = [
  { name: "الجاليات", budget: 850000, spent: 380000, color: "#2563eb" },
  { name: "المساجد", budget: 620000, spent: 310000, color: "#0891b2" },
  { name: "النسائي", budget: 380000, spent: 145000, color: "#db2777" },
  { name: "تنمية الموارد", budget: 450000, spent: 280000, color: "#d97706" },
  { name: "الموارد البشرية", budget: 1200000, spent: 720000, color: "#0284c7" },
  { name: "الاتصال", budget: 200000, spent: 85000, color: "#c026d3" },
  { name: "التطوع", budget: 150000, spent: 45000, color: "#059669" },
  { name: "الإدارة", budget: 350000, spent: 185000, color: "#475569" },
];

const BANK_ACCOUNTS = [
  { bank: "البنك الأهلي السعودي", type: "حساب جاري", accountNumber: "SA44 1000 XXXX XXXX XX01", balance: 1250000, lastTransaction: "1446/09/12", color: "#16a34a" },
  { bank: "بنك الراجحي", type: "حساب جاري", accountNumber: "SA44 8000 XXXX XXXX XX02", balance: 850000, lastTransaction: "1446/09/10", color: "#0284c7" },
  { bank: "بنك الإنماء", type: "حساب استثماري", accountNumber: "SA44 0500 XXXX XXXX XX03", balance: 620000, lastTransaction: "1446/08/28", color: "#7c3aed" },
  { bank: "البنك السعودي الفرنسي", type: "حساب وقف", accountNumber: "SA44 5500 XXXX XXXX XX04", balance: 380000, lastTransaction: "1446/07/15", color: "#d97706" },
];

/* ═════════════════════════════════════════════
   ✦ Styles
   ═════════════════════════════════════════════ */
const styles = {
  kpiRow: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 20, marginBottom: 16 },
  chartsRow: { display: "flex", gap: 16, marginBottom: 16 },
  chartTitle: { display: "flex", alignItems: "center", gap: 8, fontSize: 15, fontWeight: 700, color: C.primary, margin: "0 0 14px" },
  legendGrid: { display: "flex", flexWrap: "wrap", gap: 12, justifyContent: "center", marginTop: 8 },
  legendItem: { display: "flex", alignItems: "center", gap: 4 },
  toolbar: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" },
  searchBox: { display: "flex", alignItems: "center", gap: 8, background: C.bg, borderRadius: 8, padding: "6px 12px", border: `1px solid ${C.border}`, flex: 1, maxWidth: 320 },
  searchInput: { border: "none", background: "transparent", outline: "none", fontSize: 13, fontFamily: "'Readex Pro', sans-serif", color: C.primary, width: "100%" },
  filterSelect: { padding: "7px 12px", borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 13, fontFamily: "'Readex Pro', sans-serif", color: C.primary, background: C.white, cursor: "pointer" },
  detailRow: { fontSize: 14, color: C.primary, lineHeight: 1.6 },
  totalBar: {
    display: "flex", justifyContent: "space-between", alignItems: "center",
    padding: "14px 20px", marginTop: 12, background: C.white, borderRadius: 10,
    border: `1px solid ${C.border}`, fontSize: 14, fontWeight: 500, color: C.primary,
  },

  /* Budgets */
  budgetRow: { display: "flex", alignItems: "center", gap: 16 },
  budgetDot: { width: 12, height: 12, borderRadius: 4, flexShrink: 0 },
  budgetBarBg: { height: 10, borderRadius: 5, background: C.bg, overflow: "hidden" },
  budgetBarFill: { height: "100%", borderRadius: 5, transition: "width 0.5s" },

  /* Accounts */
  accountsGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 16 },
  accountCard: { padding: 24 },
  accountHeader: { display: "flex", alignItems: "center", gap: 20, marginBottom: 20 },
  accountIcon: { width: 52, height: 52, borderRadius: 14, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
  accountBalance: { display: "flex", flexDirection: "column", alignItems: "center", gap: 4, padding: "20px 0", borderTop: `1px solid ${C.border}`, borderBottom: `1px solid ${C.border}` },
  accountMeta: { display: "flex", justifyContent: "space-around", marginTop: 16 },
  accountMetaItem: { display: "flex", flexDirection: "column", alignItems: "center", gap: 4 },
};
